import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import {
  useLoginMutation,
  useFetchPermissionsQuery,
  useWhoamiQuery,
  usePaymentsQuery,
  useRegisterMutation,
} from "../../Pages/LoginPage/store/loginSlice";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Tab,
  Tabs,
  InputAdornment,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { styled } from "@mui/system";
import { setWhoami } from "../../store/auth/authSlice";
import {
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  Person,
} from "@mui/icons-material";
import ado from "../../img/ado.jpeg";
import Swal from "sweetalert2";

const BackgroundContainer = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#000000",
  backgroundImage: `url(${ado})`,
  backgroundAttachment: "fixed",
  backgroundSize: "cover",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center",
}));

const nacionalidadesCentroamerica = [
  "Guatemala",
  "Belice",
  "Honduras",
  "El Salvador",
  "Nicaragua",
  "Costa Rica",
  "Panamá",
];

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  backdropFilter: "blur(10px)",
  backgroundColor: "rgba(255, 255, 255, 0.8)",
  borderRadius: theme.shape.borderRadius * 2,
}));

export default function LoginForm() {
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [tab, setTab] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [nacionality, setNacionality] = useState("");
  const [name, setName] = useState("");
  const [lastname, setLastname] = useState("");
  const dispatch = useDispatch();
  const [login, { isLoading, error }] = useLoginMutation();
  const { token, isAuthenticated, hasLabsPermission, hasActivePlan } =
    useSelector((state) => state.auth);
  const { data: whoami, refetch: refetchWhoami } = useWhoamiQuery(token, {
    skip: !isAuthenticated,
  });
  const { refetch: refetchPermissions } = useFetchPermissionsQuery(token, {
    skip: !isAuthenticated,
  });
  const { refetch: refetchPayments } = usePaymentsQuery(
    { token, userId: whoami?.id },
    {
      skip: !isAuthenticated,
    }
  );
  const navigate = useNavigate();
  const [register, result] = useRegisterMutation();
  const [recaptchaValue, setRecaptchaValue] = useState(null);

  useEffect(() => {
    if (!whoami && token) {
      refetchWhoami();
    }
  }, [whoami, token, refetchWhoami]);

  useEffect(() => {
    if (isAuthenticated && token) {
      refetchPermissions();
      refetchWhoami();
      refetchPayments();
    }
  }, [isAuthenticated, token, refetchPermissions, refetchWhoami]);

  useEffect(() => {
    if (whoami) {
      dispatch(setWhoami(whoami));
  
      // Only redirect if NOT coming from menu (no flag in localStorage)
      if (!localStorage.getItem("comingFromMenu")) {
        // Check if user has active plan
        if (localStorage.getItem("hasActivePlan") === "true") {
          navigate("/menu"); // Navigate to menu if they have an active plan
        } else {
          navigate("/menu"); // Otherwise navigate to plans page
        }
      } else {
        // If coming from menu, clear the flag
        localStorage.removeItem("comingFromMenu");
      }
    }
  }, [whoami, dispatch, navigate]);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const onRecaptchaChange = (value) => {
    setRecaptchaValue(value);
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateEmail = (email) => {
    const valid = email.length === 0 || isValidEmail(email);
    setIsEmailValid(valid);
    return valid;
  };

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setUsername(newEmail);
    validateEmail(newEmail);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await login({ username, password }).unwrap();
      if (response.auth_type && response.access_token) {
        dispatch({ type: "auth/setCredentials", payload: response });

        // Add these lines to set the login flag
        sessionStorage.setItem("justLoggedIn", "true");

        // Check if the user has an active plan and set it
        // This assumes your response includes this info, adjust if needed
        if (response.hasActivePlan === true) {
          localStorage.setItem("hasActivePlan", "true");
        } else {
          localStorage.setItem("hasActivePlan", "false");
        }
      }
    } catch (err) {
      console.error("Error during login:", err);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (!recaptchaValue) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Por favor, verifica que no eres un robot",
      });
      return;
    }

    if (password !== confirmPassword) {
      console.error("Las contraseñas no coinciden");
      return;
    }

    try {
      // Realizar el registro
      await register({
        name,
        lastname,
        email: username,
        password: password,
        country: nacionality,
        role_id: 2,
      }).unwrap();

      // Cambiar a la pestaña de "Entrar"
      setTab(0);

      // Limpiar los campos de registro
      setName("");
      setLastname("");
      setUsername("");
      setPassword("");
      setConfirmPassword("");
      setNacionality("");
      setRecaptchaValue(null);

      // Mostrar mensaje de éxito
      Swal.fire({
        icon: "success",
        title: "Registro Completado",
        text: "Se ha registrado exitosamente. Por favor, inicie sesión.",
      });
    } catch (err) {
      console.error("Error durante el registro:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Hubo un problema al hacer el registro",
      });
    }
  };

  useEffect(() => {
    if (result.isLoading) {
      Swal.fire({
        title: "Registrando...",
        allowOutsideClick: false,
        timer: 3000, // 3 seconds timeout
        didOpen: () => {
          Swal.showLoading();
        },
      });
    } else if (result.isSuccess) {
      Swal.fire({
        icon: "success",
        title: "completado",
        text: "Se ha registrado exitosamente",
      });
    } else if (result.isError) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Hubo un problema al hacer el registro",
      });
    }
  }, [result, result.isLoading, result.isSuccess, result.isError]);

  if (isAuthenticated && !hasLabsPermission) {
    console.log(
      "Global Advanced Data version 1.0 - made by bluefox team"
    );
    return (
      <Box className="flex flex-col items-center">
        <CircularProgress className="text-green-500 mb-4" size={80} />
      </Box>
    );
  }

  const isNameValid = name.length > 1;
  const isLastnameValid = lastname.length > 0;
  const isPasswordValid = password.length >= 6;
  return (
    <BackgroundContainer>
      <Container component="main" maxWidth="xs">
        <StyledPaper elevation={6}>
          <Typography
            variant="h5"
            align="center"
            gutterBottom
            sx={{ fontWeight: "bold", color: "#333", marginBottom: "20px" }}
          >
            Bienvenido a El Asistente de el Transporte de Carga
          </Typography>
          <Tabs
            value={tab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{ mb: 3 }}
          >
            <Tab label="Entrar" />
            <Tab label="Registrarse" />
          </Tabs>
          <Box
            component="form"
            noValidate
            sx={{ mt: 1 }}
            onSubmit={tab === 0 ? handleLogin : handleRegister}
          >
            {tab === 1 && (
              <>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="name"
                  label="Nombre"
                  name="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  error={!isNameValid && name.length > 0}
                  helperText={
                    !isNameValid && name.length > 0
                      ? "Debe tener más de un carácter"
                      : ""
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="lastname"
                  label="Apellido"
                  name="lastname"
                  value={lastname}
                  onChange={(e) => setLastname(e.target.value)}
                  error={!isLastnameValid && lastname.length > 0}
                  helperText={
                    !isLastnameValid && lastname.length > 0
                      ? "Este campo es obligatorio"
                      : ""
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="username"
                  label="Correo Electrónico"
                  name="username"
                  autoComplete="new-username"
                  value={username}
                  onChange={handleEmailChange}
                  error={!isEmailValid}
                  helperText={
                    !isEmailValid
                      ? "Por favor ingrese un correo electrónico válido"
                      : ""
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Contraseña"
                  type={showPassword ? "text" : "password"}
                  id="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={!isPasswordValid && password.length > 0}
                  helperText={
                    !isPasswordValid && password.length > 0
                      ? "Debe tener al menos 6 caracteres"
                      : ""
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="confirmPassword"
                  label="Confirmar Contraseña"
                  type={showPassword ? "text" : "password"}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl margin="normal" required fullWidth>
                  <InputLabel id="nationality-label">Nacionalidad</InputLabel>
                  <Select
                    labelId="nationality-label"
                    id="nationality"
                    value={nacionality}
                    onChange={(e) => setNacionality(e.target.value)}
                    startAdornment={
                      <InputAdornment position="start">
                        <Person />
                      </InputAdornment>
                    }
                    label="Nacionalidad"
                  >
                    {nacionalidadesCentroamerica.map((nacionalidad) => (
                      <MenuItem key={nacionalidad} value={nacionalidad}>
                        {nacionalidad}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box
                  sx={{
                    mt: 2,
                    mb: 2,
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <ReCAPTCHA
                    sitekey="6LensfIqAAAAAMiIfzavVXoKVS-aXIXxUiVL-5PG"
                    onChange={onRecaptchaChange}
                  />
                </Box>
              </>
            )}
            {tab === 0 && (
              <>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="username"
                  label="Correo Electrónico"
                  name="username"
                  autoComplete="new-username"
                  autoFocus
                  value={username}
                  onChange={handleEmailChange}
                  error={!isEmailValid}
                  helperText={
                    !isEmailValid
                      ? "Por favor ingrese un correo electrónico válido"
                      : ""
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Contraseña"
                  type={showPassword ? "text" : "password"}
                  id="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isLoading}
            >
              {tab === 0
                ? isLoading
                  ? "Cargando..."
                  : "Iniciar Sesión"
                : "Registrarse"}
            </Button>

            {error && (
              <Typography color="error">
                {error.data?.message || "Verifique Usuario y Contraseña"}
              </Typography>
            )}
          </Box>
        </StyledPaper>
      </Container>
    </BackgroundContainer>
  );
}
